/*
  Media gallery for the TLPC product templates (pdp-tlpc-*, new-product).

  Markup: snippets/TLPC-product.liquid, snippets/tlpc-gallery-skeleton.liquid
  Styles: assets/pdp-tlpc.css ("TLPC product gallery" block)
  Zoom:   assets/tlpc-lightbox.js (opened from Flickity's staticClick below)

  This file is the ONLY owner of these carousels. The galleries hang off
  `data-tlpc-media-*` hooks rather than the theme's `data-product-single-media-*`
  ones precisely so that theme.js's Product section does not also build a
  Flickity over them - see the long comment above the English slider in
  snippets/TLPC-product.liquid for what double-initialising did to the DOM.
  Because theme.js is out of the picture, the media show/hide its `switchMedia`
  used to do on slide change is done here too (see selectSlide).

  Flickity is NOT loaded here or by the snippet. vendor.js bundles 2.3.0 and
  assets/flickity-alias.js exposes it as window.Flickity from <head>; its
  stylesheet is part of theme.css. Both are deferred earlier in the document
  and all deferred scripts run before DOMContentLoaded, so window.Flickity is
  defined by the time the handler below fires.

  The thumbnail rail IS a Flickity asNavFor instance, same as main - it is
  built here rather than left to theme.js's Product section for the same
  reason the main slider is: theme.js only ever finds the *first*
  `[data-product-single-media-thumbs]` on the page, so with two locale
  galleries it would construct Flickity twice on one element (see the double
  Flickity comment above the English slider in snippets/TLPC-product.liquid).
  Building it on the `data-tlpc-media-thumbs` hook instead means theme.js
  never finds it, so only one instance is ever created. pdp-tlpc.css then
  forces the rail's cells back into normal document flow at >=768px (see the
  "TLPC product gallery" block there) - Flickity still owns selection and
  asNavFor sync, it just isn't allowed to position the cells itself there.

  Note the division of labour with the small inline script in the snippet: that
  one resolves the locale synchronously, before first paint, because a deferred
  file like this one cannot run early enough to stop the wrong gallery being
  painted. Everything that can wait for DOMContentLoaded lives here.
*/
document.addEventListener('DOMContentLoaded', () => {
  const HIDDEN_CLASS = 'tlpc-product-gallery--hidden';
  const READY_CLASS = 'tlpc-product-gallery--ready';
  const MEDIA_HIDDEN_CLASS = 'media--hidden';

  const arabicGallery = document.querySelector('.arabic-gallery');
  const englishGallery = document.querySelector('.english-gallery');

  // One controller per gallery, kept for the lifetime of the page so switching
  // locale is a resize() rather than a teardown and rebuild.
  const controllers = new WeakMap();

  // Fade out the loading skeleton, but only once there is something real
  // underneath it. Removing it at Flickity init - which is what it used to do -
  // uncovered an empty aspect-ratio box and read as "skeleton, blank gap,
  // slider", which is exactly the shift the skeleton is supposed to hide.
  //
  // Flickity's `ready` only means the constructor finished: it is dispatched at
  // the end of activate(), synchronously, and `imagesLoaded` is wired up on that
  // same activate event - so at `ready` no slide image has loaded and no cell
  // has been sized. The moment the carousel actually occupies its space is when
  // the hero image decodes and Flickity's imagesLoaded progress handler runs
  // cellSizeChange(). So this gates on the hero image, then waits two animation
  // frames so the browser has painted the sized slider before the skeleton
  // starts fading over it.
  //
  // pdp-tlpc.css also auto-hides the skeleton after 6s, so a Flickity failure
  // can never leave it stuck over loaded product images.
  function markGalleryReady(gallery) {
    if (!gallery || gallery.dataset.tlpcGalleryReady) return;
    gallery.dataset.tlpcGalleryReady = 'true';

    const paint = () => requestAnimationFrame(() => requestAnimationFrame(() => {
      gallery.classList.add(READY_CLASS);
    }));

    const hero = gallery.querySelector('.first-slide img') ||
      gallery.querySelector('.product-single__media-slide img');

    if (!hero || hero.complete) {
      paint();
      return;
    }

    let settled = false;
    const onHeroSettled = () => {
      if (settled) return;
      settled = true;
      paint();
    };

    hero.addEventListener('load', onHeroSettled, { once: true });
    hero.addEventListener('error', onHeroSettled, { once: true });
    // A stalled image must not hold the skeleton open indefinitely.
    setTimeout(onHeroSettled, 3000);
  }

  // The full-size source for one slide, for the zoom lightbox. The zoom anchor
  // media.liquid renders when `enable_zoom` is on carries the 2048px URL; if
  // zoom is off, fall back to whatever the slide is already displaying so the
  // lightbox never opens on a blank slide.
  function zoomSourceFor(slide) {
    const link = slide.querySelector('.product-single__media-link');
    if (link && link.getAttribute('href')) return link.getAttribute('href');

    const img = slide.querySelector('img');
    if (!img) return '';
    return img.currentSrc || img.getAttribute('src') || img.dataset.src || '';
  }

  // theme.js's Product.switchMedia() did this on its own carousel's `settle`.
  // It is what pauses a video when you slide past it (deferred-media listens
  // for `theme:media:hidden`) and what the `.media--hidden:not(.is-selected)`
  // rules in theme.css use to keep offscreen controls out of the tab order.
  function switchMedia(slides, selectedSlide) {
    slides.forEach((slide) => {
      if (slide === selectedSlide) return;
      slide.classList.add(MEDIA_HIDDEN_CLASS);
      slide.dispatchEvent(new CustomEvent('theme:media:hidden', { bubbles: true }));
    });

    if (!selectedSlide) return;
    selectedSlide.classList.remove(MEDIA_HIDDEN_CLASS);
    selectedSlide.dispatchEvent(new CustomEvent('theme:media:visible', { bubbles: true }));
  }

  function buildController(gallery) {
    const slider = gallery.querySelector('[data-tlpc-media-slider]');
    if (!slider) return null;

    // Read the slides before Flickity moves them into its viewport, so the
    // order here is Liquid's order - which is also the thumbnail rail's order.
    const slides = Array.from(slider.querySelectorAll('[data-tlpc-media-wrapper]'));
    const rail = gallery.querySelector('[data-tlpc-media-thumbs]');

    if (typeof window.Flickity === 'undefined') {
      // Leave `.deferred-media-rest` alone: those slides are display:none in
      // CSS, so without a carousel the gallery still shows the hero on its own
      // rather than every image stacked up.
      console.error('[tlpc-gallery] window.Flickity is undefined - vendor.js or flickity-alias.js did not run.');
      return null;
    }

    let flkty;
    try {
      // Revealing the held-back slides and constructing Flickity in the same
      // synchronous step is deliberate: Flickity positions its cells absolutely
      // during construction, so the browser never gets a frame in which they
      // are visible and stacked. Doing the reveal from a standalone handler, as
      // an earlier version did, left the whole media set visible at full size
      // and the gallery N images tall until Flickity's first layout. Equally,
      // it must not happen *before* a construction that then throws - that is
      // how a failed init turned into "all slides stacked, last one on top".
      slides.forEach((slide) => slide.classList.remove('deferred-media-rest'));

      flkty = new Flickity(slider, {
        cellAlign: 'left',
        // `contain` has no effect alongside wrapAround, so it is not set.
        wrapAround: true,
        prevNextButtons: true,
        pageDots: true,
        draggable: true,
        adaptiveHeight: true,
        imagesLoaded: true,
        initialIndex: 0
      });
    } catch (error) {
      console.error('[tlpc-gallery] Flickity failed to initialise', error);
      slides.forEach((slide, i) => {
        if (i > 0) slide.classList.add('deferred-media-rest');
      });
      return null;
    }

    if (!flkty || !flkty.cells) {
      console.error('[tlpc-gallery] Flickity returned no instance for', slider);
      return null;
    }

    // The rail's own asNavFor instance. Kept on `data-tlpc-media-thumbs`
    // rather than `data-product-single-media-thumbs` for the same
    // double-init reason as the slider above - theme.js must never find it.
    // Flickity's AsNavFor plugin (bundled in vendor.js) owns selection sync
    // and the `is-nav-selected` class in both directions; nothing here has
    // to duplicate that.
    let railFlkty = null;
    if (rail) {
      railFlkty = new Flickity(rail, {
        asNavFor: slider,
        contain: true,
        pageDots: false,
        prevNextButtons: false,
        cellAlign: 'center',
        wrapAround: false,
        draggable: true,
        freeScroll: false,
        verticalCells: true,
        on: {
          ready: () => rail.classList.add('flickity-vertical')
        }
      });

      // Same reasoning as the slider's `.product-single__media-link` guard
      // below: the thumbnails are anchors to the full-size image (a working
      // no-JS fallback), and Flickity does not suppress anchor navigation on
      // a static click, so without this a tap on a thumbnail would leave the
      // PDP instead of just selecting it - asNavFor already does the select.
      rail.addEventListener('click', (event) => {
        if (event.target.closest('.product-single__thumbnail-link')) event.preventDefault();
      });
    }

    const controller = { gallery, slider, slides, rail, flkty, railFlkty };

    flkty.on('select', () => {
      switchMedia(slides, flkty.selectedElement);
    });

    // media.liquid lays a `.product-single__media-link` anchor over each image,
    // pointing at the 2048px file - a working no-JS zoom. Flickity does not
    // suppress anchor navigation on a static click, so without this a tap on the
    // hero would leave the PDP for a bare image URL.
    slider.addEventListener('click', (event) => {
      if (event.target.closest('.product-single__media-link')) event.preventDefault();
    });

    // Opening the lightbox from Flickity's staticClick rather than that click
    // listener is what keeps a drag from being read as a tap, and it hands us
    // the cell index directly - no index lookup to get wrong. staticClick is
    // emitted on pointerup, so it runs before the click above.
    flkty.on('staticClick', (event, pointer, cellElement, cellIndex) => {
      if (typeof cellIndex !== 'number') return;

      const clicked = slides[cellIndex];
      // A video or model slide has its own play affordance; zooming its poster
      // is not what a click there means.
      if (!clicked || clicked.dataset.type !== 'image') return;

      const lightbox = window.tlpcLightbox;
      if (!lightbox) return;

      // Paired with their slide index before the empties are dropped - mapping
      // then filtering would renumber the list and open on the wrong image.
      const zoomable = slides
        .map((slide, index) => ({ index, src: zoomSourceFor(slide) }))
        .filter((item) => item.src);

      const start = zoomable.findIndex((item) => item.index === cellIndex);
      if (start === -1) return;

      lightbox.open(zoomable.map((item) => item.src), start);
    });

    switchMedia(slides, flkty.selectedElement);

    controllers.set(gallery, controller);
    return controller;
  }

  // Show/hide galleries and init sliders.
  //
  // Visibility is a class, not an inline `style.display`, so the breakpoint
  // stays in CSS - an inline `display: block` picked at load used to survive a
  // resize past 768px and leave the desktop gallery stacked.
  function toggleGalleries(showArabic) {
    const active = showArabic ? arabicGallery : englishGallery;
    const inactive = showArabic ? englishGallery : arabicGallery;

    if (inactive) inactive.classList.add(HIDDEN_CLASS);
    if (active) active.classList.remove(HIDDEN_CLASS);
    if (!active) return;

    // Initialize sliders only when visible - Flickity measures cells, and a
    // display:none gallery measures as zero. A gallery that was built while
    // visible and then hidden has stale cell sizes, hence the resize().
    const existing = controllers.get(active);
    if (existing) {
      existing.flkty.resize();
      existing.railFlkty?.resize();
    } else {
      buildController(active);
    }

    markGalleryReady(active);
  }

  // Helper to detect Arabic locale, preferring body class over localStorage.
  // Kept in sync with the pre-paint inline script in snippets/TLPC-product.liquid.
  function getIsArabicLocale() {
    if (document.body.classList.contains('lang-ar')) return true;
    if (document.body.classList.contains('lang-en')) return false;
    const stored = (localStorage.getItem('ls-locale') || 'en').toLowerCase();
    return stored.includes('ar');
  }

  // Initial load based on locale
  toggleGalleries(getIsArabicLocale());

  // Belt and braces: if Flickity never loaded, buildController() bailed out and
  // the skeleton would otherwise sit on top of images that have already
  // painted. Reveal both galleries' real content once the page has loaded.
  window.addEventListener('load', () => {
    markGalleryReady(englishGallery);
    markGalleryReady(arabicGallery);
  });

  // Language change listener
  document.addEventListener('language:change', (e) => {
    const newLang = (e.detail?.lang || '').toLowerCase();
    if (newLang) {
      toggleGalleries(newLang.includes('ar'));
    } else {
      toggleGalleries(getIsArabicLocale());
    }
  });
});
